import request from 'utils/request'
import { updatePassword } from 'aws-amplify/auth'

export function getCognitoUser(data) {
  return request('users/me', {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function updateUser(data) {
  return request('users', {
    method: 'PUT',
    data
  }).then(([body]) => body)
}

export function updateUserPassword(data) {
  return request('users/password', {
    method: 'PUT',
    data
  }).then(([body]) => body)
}

/**
 * Changes the password of the signed in user.
 *
 * Amplify v6 works on the signed in user, so it no longer has to fetch the user
 * first. It resolves with nothing and throws on error, so the caller treats a
 * clean return as success instead of looking for v4's 'SUCCESS' string.
 *
 * @param {Object} data - Password form values.
 * @param {string} data.password - Current password.
 * @param {string} data.newPassword - New password.
 * @returns {Promise<void>}
 * @throws Rethrows the Cognito error, for example NotAuthorizedException when
 *   the current password is wrong.
 */
export async function updateCognitoUserPassword(data) {
  return updatePassword({ oldPassword: data.password, newPassword: data.newPassword })
}

export function addSystemToUser(userId, data) {
  return request(`users/${userId}/system`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function uploadUserLogo(userId, data) {
  return request(`users/${userId}/logo`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function deleteUserLogo(userId) {
  return request(`users/${userId}/logo`, {
    method: 'DELETE'
  })
}

export function sendUserOccurrence(data) {
  return request('users/support', {
    method: 'POST',
    data
  })
}

export function setUserMeasurementSystem(userId, data) {
  return request(`users/${userId}/measurementSystem`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function confirmUserNews(userId) {
  return request(`users/${userId}/confirmNews`, {
    method: 'POST'
  }).then(([body]) => body)
}

export function confirmRecipient(email, projectId) {
  return request(`users/projects/${projectId}/confirm-recipient/${encodeURIComponent(email)}`, {
    method: 'GET'
  }).then(([body]) => body)
}

export function addAlertRecipient(projectId, email, data) {
  return request(`users/projects/${projectId}/alert-recipients/${encodeURIComponent(email)}`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function confirmAlertRecipient(userId, email) {
  return request(`users/${userId}/confirmAlarmRecipient/{email}`, {
    method: 'POST',
    email
  }).then(([body]) => body)
}

export function deleteAlertRecipient(projectId, email, data) {
  return request(`users/projects/${projectId}/alert-recipients/${encodeURIComponent(email)}`, {
    method: 'DELETE',
    data
  }).then(([body]) => body)
}

export function deleteAccount(id, data) {
  return request(`users/${id}/deleteUser`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function deleteCollaborativeAccess(userId, mainEmail) {
  return request(`users/${userId}/collaborator-access/${mainEmail}`, {
    method: 'DELETE'
  }).then(([body]) => body)
}

export function addCollaborativeAccount(userId, data) {
  return request(`users/${userId}/collaborator`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function editCollaborativeAccount(userId, data) {
  return request(`users/${userId}/collaborator`, {
    method: 'PUT',
    data
  }).then(([body]) => body)
}

export function confirmTrustedUser(data) {
  return request(`users/trusted-user/confirm`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function checkTrustedUserStatus(data) {
  return request(`users/trusted-user/status`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function addTrustedUser(accountOwnerId, trustedUserEmail) {
  return request(`users/trusted-user/${accountOwnerId}/${trustedUserEmail}`, {
    method: 'POST'
  }).then(([body]) => body)
}

export function manageTrustedUsers(data) {
  return request(`users/trusted-user/manage`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function deleteSystemFromUser(data) {
  return request(`users/device-project`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}

export function deleteMultipleSystems(data) {
  return request(`users/multiple-devices`, {
    method: 'POST',
    data
  }).then(([body]) => body)
}
